import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full" />
            <span className="font-bold text-lg">Factual Medical Diagnostic Centre</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-white hover:text-sky-300">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-slate-300">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              By accessing and using the services of Factual Medical Diagnostic Centre, you accept and agree 
              to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. Description of Services</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Factual Medical Diagnostic Centre provides medical laboratory and diagnostic services, including 
              but not limited to blood tests, urinalysis, and other clinical laboratory examinations. Our services 
              include in-centre sample collection and home sample collection options.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. User Accounts</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">When you create an account with us, you agree to:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mb-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. Medical Disclaimer</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Our laboratory results are intended to assist healthcare providers in making clinical decisions. 
              Results should be interpreted by qualified medical professionals. We do not provide medical 
              diagnoses or treatment recommendations. Always consult with your healthcare provider regarding 
              any test results or health concerns.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Booking and Payments</h2>
            <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mb-6 space-y-2">
              <li>All bookings are subject to availability and confirmation</li>
              <li>Prices are quoted in Nigerian Naira (₦) and are subject to change</li>
              <li>Payment is required before or at the time of service</li>
              <li>We accept bank transfers, card payments, and cash</li>
              <li>Refunds are subject to our refund policy</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. Sample Collection</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Patients must follow all pre-test instructions provided (e.g., fasting requirements). 
              Failure to follow instructions may affect test accuracy. Home sample collection is available 
              within our service areas and may incur additional charges.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Results Delivery</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Results are delivered through our secure online portal, email, or in-person collection. 
              Turnaround times are estimates and may vary based on test complexity and laboratory workload. 
              Critical values will be communicated immediately to the patient and/or referring physician.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              To the fullest extent permitted by law, Factual Medical Diagnostic Centre shall not be liable 
              for any indirect, incidental, special, consequential, or punitive damages arising from your 
              use of our services. Our total liability shall not exceed the amount paid for the specific 
              service in question.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Intellectual Property</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              All content, trademarks, logos, and intellectual property on our platform belong to 
              Factual Medical Diagnostic Centre. You may not reproduce, distribute, or use any content 
              without our prior written consent.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">10. Changes to Terms</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective 
              immediately upon posting on our website. Your continued use of our services constitutes 
              acceptance of the modified terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">11. Governing Law</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              These Terms of Service shall be governed by and construed in accordance with the laws of 
              the Federal Republic of Nigeria. Any disputes shall be subject to the exclusive jurisdiction 
              of the courts in Ogun State, Nigeria.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">12. Contact Information</h2>
            <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
              <p className="text-slate-700 dark:text-slate-300 mb-2">
                For questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-slate-700 dark:text-slate-300"><strong>Email:</strong> FACTUALMEDICALDIAGNOSTICCENTRE@GMAIL.COM</p>
              <p className="text-slate-700 dark:text-slate-300"><strong>Phone:</strong> 09049816536, 08025072241, 08141571388</p>
              <p className="text-slate-700 dark:text-slate-300"><strong>Address:</strong> No 502, Arogun Bus Stop, Mowe/Ofada Road, Adjacent Medicplus Hospital, Ogun State, Nigeria</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">&copy; {new Date().getFullYear()} Factual Medical Diagnostic Centre. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
